import React, { useEffect, useRef, useState, } from 'react';

import { Button, Spin } from 'antd';
import { useSelector } from 'react-redux';

import { PrivateWrapper } from '../../../PrivateWrapper';

import { Lock } from '../../../assets/icons';
import AuthForm from '../../../components/AuthForm/AuthForm';
import AuthInfo from '../../../components/AuthInfo/AuthInfo';

import StepOne from './StepOne';
import StepTwo from './StepTwo';
import StepThree from './StepThree';
import StepFour from './StepFour';
import UploadID from './UploadID';
import StepSix from './StepSix';
import EntityDocs from './EntityDocs';
// import StepFive from './StepFive'

import './Verification.scss';
import { useNavigate } from 'react-router-dom';
import { applyStoredFirstTouchUtmParams } from '../../../utils/utmTracker';
import { ACCOUNT_TYPES } from '../../../constants/accountType.constants';
import useGetUserProfile from '../../../Hooks/useGetUserProfile';
import { VERIFICATION_STEP_CONTENT } from '../../../utils/constant';

// Pure lookup: given where the user left off (counter) and their profile, which step should
// Verification resume on? Kept outside the component/effect to keep both easy to read in
// isolation instead of one large branchy effect body.
const determineStartingStep = (counter, userData, accountType) => {
  if (userData?.userData?.citizenshipStatus === 'Non US Resident') {
    return null;
  }

  const counterStr = counter.toString();

  if ((counterStr === '4' || counterStr === '5') && !userData?.userData?.isKYCVerified) {
    return 'StepSix';
  }

  switch (counterStr) {
    case '0': {
      if (accountType !== ACCOUNT_TYPES.ENTITY) {
        return 'StepOne';
      }
      const hasEntityDocs = (userData?.userData?.entityProfile?.entityDocs?.length ?? 0) > 0;
      return hasEntityDocs ? 'StepTwo' : 'EntityDocs';
    }
    case '1':
      return 'StepTwo';
    case '2':
      return 'StepThree';
    case '3': {
      const identificationDocs = userData?.userData?.identificationDocs;
      const isDwollaVerified = userData?.userData?.isDwollaVerified;
      const needsIdUpload = isDwollaVerified === false && (!identificationDocs || Object.keys(identificationDocs).length === 0);
      return needsIdUpload ? 'UploadID' : 'StepFour';
    }
    case '4':
    case '5':
      return 'StepSix';
    default:
      return 'StepOne';
  }
};

const Verification = () => {
  // null until the correct starting step is determined below — prevents flashing the
  // Individual StepOne before an Entity account's profile finishes loading.
  const [activeStep, setActiveStep] = useState(null);
  const [viewForm, setViewForm] = useState(true);
  const counter = useSelector((state) => state.userData.counter);
  const userData = useSelector((state) => state.userData);
  const accountType = useSelector((state) => state.userData.userData?.accountType);
  const isProfileLoaded = useSelector((state) => state.userData.isProfileLoaded);
  const navigate = useNavigate();
  const hasInitializedStep = useRef(false);

  // Safety net: if the profile fetch never resolves (e.g. network failure), don't strand
  // the user on a spinner forever — fall back to the decision effect's default after a wait.
  const [forceProceed, setForceProceed] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setForceProceed(true), 8000);
    return () => clearTimeout(timer);
  }, []);

  // StepOne (unlike StepTwo/StepThree/StepFour) doesn't fetch the profile itself, so on a
  // refresh while sitting on StepOne, accountType was never (re)loaded and the step-decision
  // effect below defaulted to the Individual flow even for Entity accounts. Fetching here
  // guarantees fresh data (or reuses an in-flight fetch) regardless of which step is active.
  useGetUserProfile();
  useEffect(() => {
    // Skip UTM parameter handling for Non US Residents
    if (userData?.userData?.citizenshipStatus === "Non US Resident") {
      return;
    }
    
    const utmContentMap = {
      StepOne: 'Pin_Setup_Done',
      EntityDocs: 'Entity_Documentation_Completed',
      StepTwo: 'Identity_Verification_Completed',
      StepThree: 'Contact_Info_Added',
      StepFour: 'SSN_DOB_Added',
      UploadID: 'ID_Upload_Screen',
      // StepFive: 'Accredited_Check_Completed',
      StepSix: 'Agreements_Completed',
    };
  
    const stepParam = utmContentMap[activeStep] || 'default';
    const params = new URLSearchParams({
      utm_source: 'onboarding',
      utm_medium: 'web',
      utm_content: stepParam,
    });
    applyStoredFirstTouchUtmParams(params);

    navigate(`/sign-up/verification?${params.toString()}`, { replace: true });
  }, [activeStep, navigate]);
  useEffect(() => {
    // Wait for a confirmed profile load (fresh fetch above, or rehydrated persisted state)
    // before deciding which step to resume on — otherwise this can run with default/stale
    // redux state (e.g. accountType missing) right after a refresh. Only decide once; later
    // profile updates (e.g. after submitting a step) shouldn't reset activeStep. forceProceed
    // is a timeout fallback so a failed/hanging fetch doesn't strand the user indefinitely.
    //
    // At counter 0 specifically, some sign-in paths (e.g. login's validate-pin, used when
    // resuming onboarding from a fresh tab/session) mark isProfileLoaded true from a response
    // that hasn't populated accountType yet, ahead of the guaranteed profile fetch above.
    // accountType === undefined reads as "not Entity" below, which happens to be the right
    // answer for Individual accounts but silently misroutes brand-new Entity accounts to the
    // Individual flow. Hold off deciding until accountType is actually known (or forceProceed
    // fires) so that fetch gets a chance to supply it first.
    const needsAccountType = counter.toString() === '0' && accountType === undefined;
    if (hasInitializedStep.current || (!forceProceed && (!isProfileLoaded || needsAccountType))) {
      return;
    }
    hasInitializedStep.current = true;

    // determineStartingStep returns null for Non US Residents (no step logic applies to them);
    // fall back to StepOne rather than leaving activeStep at null, or they'd be stuck on the
    // loading spinner below indefinitely.
    setActiveStep(determineStartingStep(counter, userData, accountType) ?? 'StepOne');
  }, [isProfileLoaded, forceProceed, counter, userData, accountType]);
  const { infoTitle, infoDescription, formTitle, formDescription } = VERIFICATION_STEP_CONTENT[activeStep] ?? {};

  return (
    <PrivateWrapper>
      <div className='auth'>
        <AuthInfo
          className='hide'
          title={infoTitle}
          description={infoDescription}
          note={
            <>
              <Lock marginRight='10px' />
              <span className='fw-400'>All of your information is encrypted and secure.</span>
            </>
          }
        >
          <div className='mobile-action' onClick={() => setViewForm(true)}>
            <Button className='w-100'>Get Started</Button>
          </div>
        </AuthInfo>
        <AuthForm className={viewForm ? 'show' : null} title={formTitle} description={formDescription}>
          <>
            {activeStep === null ? (
              <Spin className='w-100 text-center' spinning />
            ) : (
              <>
                <div className={`progress-bar ${activeStep.toLowerCase()}`} />
                {activeStep === 'StepOne' && <StepOne nextAction={setActiveStep} />}
                {activeStep === 'EntityDocs' && <EntityDocs nextAction={setActiveStep} />}
                {activeStep === 'StepTwo' && <StepTwo nextAction={setActiveStep} />}
                {activeStep === 'StepThree' && <StepThree nextAction={setActiveStep} />}
                {activeStep === 'StepFour' && <StepFour nextAction={setActiveStep} />}
                {activeStep === 'UploadID' && <UploadID nextAction={setActiveStep} />}
                {/* {activeStep === 'StepFive' && <StepFive nextAction={setActiveStep} />} */}
                {activeStep === 'StepSix' && <StepSix nextAction={setActiveStep} />}
              </>
            )}
          </>
        </AuthForm>
      </div>
    </PrivateWrapper>
  );
};

export default Verification;
